/**
 * Чистая логика экрана «Новое сообщение» и выбора участников группы (аудит удобства
 * 2026-10-04, вариант A).
 *
 * <p>Здесь ни React, ни сети: кто кому кем приходится, в каком порядке стоят контакты, что
 * показывать по набранной строке. Всё это раньше жило внутри `ChatListScreen` и проверялось
 * только глазами на устройстве — например, что строка найденного человека, которому уже ушла
 * заявка, не предлагает отправить её второй раз.
 */

import type { ContactDto, ContactRequestDto, UserPublic } from '../api/types';

/** Кем человек приходится смотрящему — от этого зависит кнопка в его строке. */
export type PersonRelation = 'self' | 'contact' | 'outgoing' | 'incoming' | 'stranger';

export type RelationIndex = {
  selfId: string | null | undefined;
  contactIds: ReadonlySet<string>;
  outgoingIds: ReadonlySet<string>;
  incomingIds: ReadonlySet<string>;
};

export function relationOf(userId: string, index: RelationIndex): PersonRelation {
  if (index.selfId && userId === index.selfId) return 'self';
  if (index.contactIds.has(userId)) return 'contact';
  // Встречная заявка важнее исходящей: если оба успели позвать друг друга, правильное действие
  // одно — принять, а не «заявка отправлена» без кнопки.
  if (index.incomingIds.has(userId)) return 'incoming';
  if (index.outgoingIds.has(userId)) return 'outgoing';
  return 'stranger';
}

/** Висящие заявки по направлениям. Принятые и отклонённые сервер не отдаёт, но фильтр дешевле догадки. */
export function pendingRequests(requests: readonly ContactRequestDto[]): {
  incoming: ContactRequestDto[];
  outgoing: ContactRequestDto[];
} {
  const incoming: ContactRequestDto[] = [];
  const outgoing: ContactRequestDto[] = [];
  for (const r of requests) {
    if (r.status !== 'PENDING') continue;
    (r.direction === 'INCOMING' ? incoming : outgoing).push(r);
  }
  return { incoming, outgoing };
}

export function buildRelationIndex(
  selfId: string | null | undefined,
  contacts: readonly ContactDto[],
  requests: readonly ContactRequestDto[],
): RelationIndex {
  const { incoming, outgoing } = pendingRequests(requests);
  return {
    selfId,
    contactIds: new Set(contacts.map((c) => c.userId)),
    incomingIds: new Set(incoming.map((r) => r.userId)),
    outgoingIds: new Set(outgoing.map((r) => r.userId)),
  };
}

/**
 * Строка поиска без приставок ника и регистра. «@anna», «#anna» и «Anna» ищут одно и то же:
 * ник в приложении показывается то с «#», то с «@», и человек набирает тот знак, который видел.
 */
export function normalizeQuery(query: string): string {
  return query.trim().replace(/^[@#]+/, '').toLocaleLowerCase();
}

/** Совпадение без учёта регистра и «ё/е» — их путают в именах чаще всего. */
function folded(value: string): string {
  return value.toLocaleLowerCase().replace(/ё/g, 'е');
}

/** Подходит ли контакт к строке: по любой части имени или по нику с начала. */
export function contactMatches(contact: Pick<ContactDto, 'displayName' | 'publicHandle'>, query: string): boolean {
  const q = folded(normalizeQuery(query));
  if (!q) return true;
  if (folded(contact.displayName).includes(q)) return true;
  const handle = (contact.publicHandle ?? '').toLocaleLowerCase();
  return handle.length > 0 && handle.startsWith(q);
}

/**
 * Контакты в порядке экрана: кто в сети — сверху, внутри групп — по алфавиту. Так делают все
 * мессенджеры со списком контактов: открыв «Новое сообщение», чаще всего пишут тому, кто
 * сейчас доступен.
 */
export function sortContactsForList<T extends Pick<ContactDto, 'userId' | 'displayName'>>(
  contacts: readonly T[],
  isOnline: (contact: T) => boolean,
): T[] {
  return [...contacts].sort((a, b) => {
    const ao = isOnline(a) ? 0 : 1;
    const bo = isOnline(b) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    return a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
  });
}

/**
 * Результаты сервера под локальными: без тех, кто уже показан выше среди контактов, и без
 * самого смотрящего — себя добавить нельзя, а строка «Это вы» в результатах только мешает.
 */
export function serverResultsBelowLocal(
  results: readonly UserPublic[],
  shownIds: ReadonlySet<string>,
  selfId: string | null | undefined,
): UserPublic[] {
  return results.filter((p) => !shownIds.has(p.id) && p.id !== selfId);
}

/**
 * Ссылка-приглашение. С ником — личная страница `…/u/<ник>`: по ней человек сразу попадает в
 * диалог со звавшим. Без ника — общая ссылка на приложение: пригласить можно и так, а ник
 * экран предложит завести (без него звавшего по ссылке не найти).
 */
export function inviteLink(handle: string | null | undefined, origin: string): { url: string; personal: boolean } {
  const base = origin.replace(/\/$/, '');
  const h = (handle ?? '').trim().replace(/^[@#]+/, '');
  if (h) return { url: `${base}/u/${encodeURIComponent(h)}`, personal: true };
  return { url: `${base}/`, personal: false };
}

/** Участник новой группы: выбран — убрать, не выбран — добавить. Себя не добавляем никогда. */
export function toggleMember<T extends { id: string }>(
  selected: readonly T[],
  person: T,
  selfId: string | null | undefined,
): T[] {
  if (selfId && person.id === selfId) return [...selected];
  return selected.some((p) => p.id === person.id)
    ? selected.filter((p) => p.id !== person.id)
    : [...selected, person];
}

/** «Создать» доступна, только когда есть кого звать и как назвать (п. 14 аудита: молча не создавалось). */
export function canCreateGroup(name: string, selectedCount: number): boolean {
  return name.trim().length > 0 && selectedCount > 0;
}
